import { Link } from "@tanstack/react-router";
import { SEO } from "@/components/SEO";

export function WhatIsEmailAuthenticationGuide() {
  return (
    <div>
      <SEO
        title="What is Email Authentication? SPF, DKIM, DMARC Explained"
        description="A plain-English explanation of SPF, DKIM, and DMARC email authentication. Learn how these three protocols work together to protect your domain and improve email deliverability."
        canonical="/guides/what-is-email-authentication"
        ogType="article"
      />

      {/* Hero */}
      <section className="relative overflow-hidden border-b bg-gradient-to-b from-indigo-50/50 to-transparent py-16 dark:from-indigo-950/20">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <Link
            to="/resources"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
            Back to Help Center
          </Link>
          <h1 className="mt-8 text-4xl font-bold tracking-tight sm:text-5xl">
            What is Email Authentication?
          </h1>
          <p className="mt-6 text-xl text-muted-foreground leading-relaxed">
            A plain-English explanation of SPF, DKIM, and DMARC — no technical jargon required.
          </p>
        </div>
      </section>

      {/* Content */}
      <article className="py-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">

          {/* Section 1 */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
              The Problem with Email
            </h2>
            <div className="mt-6 space-y-4 text-lg text-muted-foreground leading-relaxed">
              <p>
                Email was invented in the 1970s, before security was a major concern. The original
                design had a fatal flaw: <strong className="text-foreground">anyone can claim to be anyone</strong>.
              </p>
              <p>
                When you receive an email "from" your bank, your email provider has no built-in way
                to verify it actually came from the bank. Scammers exploit this every day to send
                phishing emails, fake invoices, and fraud.
              </p>
              <p>
                Email authentication solves this problem by letting domain owners prove which emails
                are really from them.
              </p>
            </div>
          </section>

          {/* Divider */}
          <hr className="my-12 border-border" />

          {/* Section 2 */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
              The Three Pillars of Email Authentication
            </h2>

            <div className="mt-10 space-y-6">
              <div className="rounded-2xl border bg-gradient-to-br from-blue-50 to-indigo-50 p-8 dark:from-blue-950/30 dark:to-indigo-950/30">
                <h3 className="text-2xl font-bold text-blue-700 dark:text-blue-300">
                  1. SPF (Sender Policy Framework)
                </h3>
                <p className="mt-4 text-lg">
                  <strong>What it does:</strong> Lists which email servers are allowed to send email
                  for your domain.
                </p>
                <div className="mt-4 rounded-lg bg-white/80 p-4 dark:bg-black/20">
                  <p className="font-medium">Real-world analogy:</p>
                  <p className="mt-1 text-muted-foreground">
                    Imagine a bouncer at a club with a guest list. SPF is the list of email servers
                    that are "on the list" and allowed to send email as your domain. If a server
                    isn't on the list, email providers know something's fishy.
                  </p>
                </div>
                <p className="mt-4 text-sm text-muted-foreground">
                  SPF is a simple TXT record in your DNS that looks like: <br />
                  <code className="rounded bg-black/10 px-2 py-1 dark:bg-white/10">
                    v=spf1 include:_spf.google.com -all
                  </code>
                </p>
              </div>

              <div className="rounded-2xl border bg-gradient-to-br from-green-50 to-emerald-50 p-8 dark:from-green-950/30 dark:to-emerald-950/30">
                <h3 className="text-2xl font-bold text-green-700 dark:text-green-300">
                  2. DKIM (DomainKeys Identified Mail)
                </h3>
                <p className="mt-4 text-lg">
                  <strong>What it does:</strong> Adds a digital signature to every email that proves
                  it hasn't been tampered with.
                </p>
                <div className="mt-4 rounded-lg bg-white/80 p-4 dark:bg-black/20">
                  <p className="font-medium">Real-world analogy:</p>
                  <p className="mt-1 text-muted-foreground">
                    Think of DKIM like a wax seal on a letter. If the seal is broken, you know
                    someone tampered with it. DKIM uses cryptography to create an unbreakable
                    digital seal on your emails.
                  </p>
                </div>
                <p className="mt-4 text-sm text-muted-foreground">
                  DKIM requires your email provider to sign outgoing emails, and a public key in
                  your DNS for verification.
                </p>
              </div>

              <div className="rounded-2xl border bg-gradient-to-br from-purple-50 to-violet-50 p-8 dark:from-purple-950/30 dark:to-violet-950/30">
                <h3 className="text-2xl font-bold text-purple-700 dark:text-purple-300">
                  3. DMARC (Domain-based Message Authentication)
                </h3>
                <p className="mt-4 text-lg">
                  <strong>What it does:</strong> Tells email providers what to do when SPF or DKIM
                  fail, and sends you reports about who's using your domain.
                </p>
                <div className="mt-4 rounded-lg bg-white/80 p-4 dark:bg-black/20">
                  <p className="font-medium">Real-world analogy:</p>
                  <p className="mt-1 text-muted-foreground">
                    DMARC is like a security policy. It says "if someone tries to send email as my
                    domain and they're not authorized, reject it (or quarantine it)." It also sends
                    you reports so you can see who's trying to impersonate you.
                  </p>
                </div>
                <p className="mt-4 text-sm text-muted-foreground">
                  DMARC policies: <strong>none</strong> (monitor only), <strong>quarantine</strong>{" "}
                  (send to spam), or <strong>reject</strong> (block completely).
                </p>
              </div>
            </div>
          </section>

          {/* Divider */}
          <hr className="my-12 border-border" />

          {/* Section 3 */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
              How They Work Together
            </h2>
            <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
              Think of these three as layers of security:
            </p>

            <div className="mt-8 space-y-4">
              <div className="flex items-start gap-4">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-700 font-bold dark:bg-blue-900/30 dark:text-blue-300">
                  1
                </div>
                <div>
                  <span className="font-semibold">SPF</span>
                  <span className="text-muted-foreground"> verifies the sending server is authorized</span>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-green-100 text-green-700 font-bold dark:bg-green-900/30 dark:text-green-300">
                  2
                </div>
                <div>
                  <span className="font-semibold">DKIM</span>
                  <span className="text-muted-foreground"> verifies the email content hasn't been changed</span>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-purple-100 text-purple-700 font-bold dark:bg-purple-900/30 dark:text-purple-300">
                  3
                </div>
                <div>
                  <span className="font-semibold">DMARC</span>
                  <span className="text-muted-foreground"> enforces the rules and reports violations</span>
                </div>
              </div>
            </div>

            <p className="mt-8 text-lg text-muted-foreground leading-relaxed">
              When all three are properly configured, email providers trust your emails and
              deliver them to the inbox instead of spam.
            </p>
          </section>

          {/* Divider */}
          <hr className="my-12 border-border" />

          {/* Section 4 */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
              Do I Really Need All Three?
            </h2>
            <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
              <strong className="text-foreground">Yes.</strong> While each provides some protection on its own, they're
              designed to work together:
            </p>
            <div className="mt-6 space-y-3">
              <div className="flex items-start gap-3">
                <svg className="h-6 w-6 flex-shrink-0 text-amber-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                </svg>
                <span className="text-lg text-muted-foreground">SPF alone can be bypassed by forwarded emails</span>
              </div>
              <div className="flex items-start gap-3">
                <svg className="h-6 w-6 flex-shrink-0 text-amber-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                </svg>
                <span className="text-lg text-muted-foreground">DKIM alone doesn't tell providers what to do with failures</span>
              </div>
              <div className="flex items-start gap-3">
                <svg className="h-6 w-6 flex-shrink-0 text-amber-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                </svg>
                <span className="text-lg text-muted-foreground">DMARC requires at least one of SPF or DKIM to work</span>
              </div>
            </div>
            <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
              For the best email deliverability and security, you need all three configured
              correctly.
            </p>
          </section>

          {/* Divider */}
          <hr className="my-12 border-border" />

          {/* Section 5 */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
              Check Your Domain Now
            </h2>
            <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
              Want to see if your domain has email authentication set up? Use our free checker:
            </p>

            <div className="mt-10 flex justify-center">
              <Link
                to="/tools"
                className="inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 px-8 py-4 text-lg font-semibold text-white shadow-lg transition-all hover:shadow-xl hover:scale-105"
              >
                Check Your Domain
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </Link>
            </div>
          </section>

          {/* CTA Box */}
          <section className="rounded-2xl bg-gradient-to-br from-indigo-50 to-purple-50 p-8 dark:from-indigo-950/50 dark:to-purple-950/50 sm:p-10">
            <h3 className="text-2xl font-bold">Ready to set up email authentication?</h3>
            <p className="mt-4 text-lg text-muted-foreground">
              GetSent makes it easy to configure SPF, DKIM, and DMARC — even if you're not
              technical. We'll guide you through each step.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/register"
                className="inline-flex rounded-lg bg-indigo-600 px-6 py-3 text-base font-semibold text-white hover:bg-indigo-500 transition-colors"
              >
                Get Started Free
              </Link>
              <Link
                to="/resources"
                className="inline-flex rounded-lg border border-border bg-background px-6 py-3 text-base font-semibold hover:bg-muted transition-colors"
              >
                Explore More Guides
              </Link>
            </div>
          </section>

        </div>
      </article>
    </div>
  );
}
